import { useCallback, useMemo, useState } from 'react'
import { Button, ToggleGroup } from '~/components/ui'
import { getSuffix, LotSizeUnit } from '~/models'
import { cn } from '~/utils'
import MapMinMaxFilter from './MinMax'

export default function MapLotSizeFilter({
  className,
  filterValues,
  lotSizeUnit = 'acres',
  updateFilter,
}: {
  className?: string
  filterValues: (number | undefined)[]
  lotSizeUnit?: LotSizeUnit
  updateFilter: (updates: Record<string, any>) => void
}) {
  const [resetKey, setResetKey] = useState(0)

  const suffix = useMemo(() => getSuffix(lotSizeUnit), [lotSizeUnit])

  const handleUnitChange = useCallback(
    (newUnit: LotSizeUnit) => {
      if (newUnit === lotSizeUnit) return

      updateFilter({
        lotSizeUnit: newUnit,
      })
    },
    [lotSizeUnit, updateFilter]
  )

  const handleReset = useCallback(() => {
    updateFilter({ acres: undefined, lotSizeUnit: 'acres' })
    setResetKey((key) => key + 1)
  }, [updateFilter])

  const hasValues = filterValues.filter((v) => v).length > 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm">
          Lot Size
          {hasValues && (
            <Button
              variant="link"
              size="none"
              className="ml-2"
              onClick={handleReset}>
              (reset)
            </Button>
          )}
        </p>
        <ToggleGroup
          className="h-7"
          onValueChange={(value) => {
            if (value) handleUnitChange(value as LotSizeUnit)
          }}
          type="single"
          value={lotSizeUnit}>
          <ToggleGroup.Item
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            value="acres">
            Acres
          </ToggleGroup.Item>
          <ToggleGroup.Item
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            value="sf">
            SF
          </ToggleGroup.Item>
        </ToggleGroup>
      </div>

      <MapMinMaxFilter
        end={suffix}
        filterValues={filterValues}
        hideReset
        key={resetKey}
        label=""
        name="acres"
        updateFilter={updateFilter}
      />
    </div>
  )
}
